<template>
  <ion-page>
    <ion-content>
      <div class="banner-container">
        <BannerComponent
          :src="isMobile ? mobileBannerSrc : desktopBannerSrc"
          title="ACCOUNT"
          description="Choose how you'd like us to keep in touch with you."
        />
      </div>
      <ion-grid :fixed="true" class="ion-padding">
        <ion-row>
          <ion-col size-md="6" size-xs="12">
            <h2>Communication preference</h2>
            <p>Select how you'd like us to contact you about your requests.</p>

            <ion-item lines="none">
              <ion-checkbox
                v-model="prefersEmail"
                :disabled="!email"
                slot="start"
              ></ion-checkbox>
              <ion-label>
                Email
                <p v-if="email">{{ email }}</p>
                <p v-else>No email address on file</p>
              </ion-label>
            </ion-item>

            <ion-item lines="none">
              <ion-checkbox
                v-model="prefersPhone"
                :disabled="!cellPhone"
                slot="start"
              ></ion-checkbox>
              <ion-label>
                Phone
                <p v-if="cellPhone">{{ cellPhone }}</p>
                <p v-else>No phone number on file</p>
              </ion-label>
            </ion-item>

            <ion-note v-if="errorMessage" color="danger">{{
              errorMessage
            }}</ion-note>
            <ion-note v-if="successMessage" color="success">{{
              successMessage
            }}</ion-note>

            <ion-button
              expand="block"
              class="ion-margin-top"
              :disabled="isSaving || isLoading"
              @click="savePreferences"
            >
              Save
            </ion-button>
          </ion-col>
        </ion-row>
      </ion-grid>
      <FooterComponent />
    </ion-content>
  </ion-page>
</template>

<script setup>
import {
  IonPage,
  IonContent,
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonButton,
  IonNote,
} from '@ionic/vue';
import BannerComponent from '@/components/BannerComponent.vue';
import FooterComponent from '@/components/FooterComponent.vue';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useBrandingStore } from '../store/branding';
import {
  fetchPreferences,
  updatePreferences,
} from '@/services/vapcore/preferences';
import * as Sentry from '@sentry/vue';

const desktopBannerSrc = ref('');
const mobileBannerSrc = ref('');
const screenWidth = ref(window.innerWidth);
const isMobile = computed(() => {
  return screenWidth.value < 768;
});
const handleResize = () => {
  screenWidth.value = window.innerWidth;
};
const brandingStore = useBrandingStore();

desktopBannerSrc.value = brandingStore.brand.desktop_banner_8;
mobileBannerSrc.value = brandingStore.brand.mobile_banner_8;

const email = ref('');
const cellPhone = ref('');
const prefersEmail = ref(false);
const prefersPhone = ref(false);
const isLoading = ref(false);
const isSaving = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

async function loadPreferences() {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const data = await fetchPreferences();
    email.value = data.email;
    cellPhone.value = data.cellPhone;
    prefersEmail.value = data.prefersEmail;
    prefersPhone.value = data.prefersPhone;
  } catch (error) {
    Sentry.captureException(error);
    errorMessage.value =
      'We could not load your communication preference. Please try again later.';
  } finally {
    isLoading.value = false;
  }
}

async function savePreferences() {
  errorMessage.value = '';
  successMessage.value = '';

  if (!prefersEmail.value && !prefersPhone.value) {
    errorMessage.value = 'Please select at least one way for us to contact you.';
    return;
  }

  isSaving.value = true;
  try {
    await updatePreferences({
      prefersEmail: prefersEmail.value,
      prefersPhone: prefersPhone.value,
    });
    successMessage.value = 'Your communication preference has been saved.';
  } catch (error) {
    Sentry.captureException(error);
    errorMessage.value =
      'We could not save your communication preference. Please try again.';
  } finally {
    isSaving.value = false;
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize);
  loadPreferences();
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style lang="scss" scoped>
.ion-page {
  margin-top: 125px;
}

@media only screen and (max-width: 768px) {
  .ion-page {
    margin-top: 42px;
  }
}

.banner-container {
  position: relative;
}
</style>
